import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import { DynamicRecaptcha } from "../../recaptcha/DynamicRecaptcha";
import { ErrorBox } from "../support/ErrorBox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import { FormikProps } from "formik";
import InputLabel from "@mui/material/InputLabel";
import Link from "next/link";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { EmployeeAccessData as Type } from "../employee-model";
import Typography from "@mui/material/Typography";
import { useCargillBusiness } from "../useCargillBusiness";
import { useCargillRegions } from "../useCargillRegions";
import { useCargillRoles } from "../useCargillRoleList";
import { useSiteKey } from "../../recaptcha/useSiteKey";
import { useTranslation } from "next-i18next";

const termsAndConditionsUrl = "https://www.cargill.com/page/website-terms";
interface Props {
  formik: FormikProps<Type>;
}

/**
 * Employee Access step / screen.
 */
export function EmployeeAccessStep({ formik }: Props) {
  const { t, i18n } = useTranslation();
  const sitekey = useSiteKey();

  const cargillRoleData = useCargillRoles();
  const cargillRegionData = useCargillRegions();
  const cargillBusinessData = useCargillBusiness();

  /**
   * Set the reCAPTCHA token
   */
  function setRecaptchaToken(token: any) {
    if (token && token.length > 0) {
      formik.setFieldValue("accessToken", token);
    } else {
      formik.setFieldValue("accessToken", "");
    }
  }

  /**
   * Clear the reCAPTCHA token
   */
  function clearRecaptchaToken() {
    formik.setFieldValue("accessToken", "");
  }

  return (
    <Box>
      <Box
        sx={{
          marginBottom: "20px",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: "1.75em",
            fontWeight: "bold",
          }}
        >
          {t("common:common.request_access")}
        </Typography>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            marginBottom: "20px",
          }}
        >
          {/* DS ID */}
          <InputLabel sx={{ textAlign: "left", mt: "1rem" }} htmlFor="dsId">
            {t("request_access:employee_access.ds_id")}
          </InputLabel>
          <TextField
            fullWidth
            id="dsId"
            name="dsId"
            value={formik.values.dsId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.dsId && formik.touched.dsId && (
            <ErrorBox>{formik.errors.dsId}</ErrorBox>
          )}

          {/* Email */}
          <InputLabel sx={{ textAlign: "left", mt: "1rem" }} htmlFor="email">
            {t("common:common.email_address")}
          </InputLabel>
          <TextField
            fullWidth
            id="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.email && formik.touched.email && (
            <ErrorBox>{formik.errors.email}</ErrorBox>
          )}

          {/* First Name */}
          <InputLabel
            sx={{ textAlign: "left", mt: "1rem" }}
            htmlFor="firstName"
          >
            {t("common:common.first_name")}
          </InputLabel>
          <TextField
            fullWidth
            id="firstName"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.firstName && formik.touched.firstName && (
            <ErrorBox>{formik.errors.firstName}</ErrorBox>
          )}

          {/* Last Name */}
          <InputLabel sx={{ textAlign: "left", mt: "1rem" }} htmlFor="lastName">
            {t("common:common.last_name")}
          </InputLabel>
          <TextField
            fullWidth
            id="lastName"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.lastName && formik.touched.lastName && (
            <ErrorBox>{formik.errors.lastName}</ErrorBox>
          )}

          {/* Cargill Role */}
          <Box sx={{ textAlign: "left" }}>
            <InputLabel
              sx={{ textAlign: "left", mt: "1rem" }}
              htmlFor="cargillRole"
            >
              {t("request_access:employee_access.cargill_role")}
            </InputLabel>
            <FormControl fullWidth>
              <Select
                sx={{
                  paddingLeft: "0",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                }}
                id="cargillRole"
                name="cargillRole"
                value={formik.values.cargillRole}
                onChange={formik.handleChange}
              >
                {cargillRoleData.map(
                  (item: { name: string }, index: number) => (
                    <MenuItem key={index} value={item.name}>
                      {item.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Box>

          {/* Cargill Business */}
          <Box sx={{ textAlign: "left" }}>
            <InputLabel
              sx={{ textAlign: "left", mt: "1rem" }}
              htmlFor="cargillBusinessEmployee"
            >
              {t("request_access:employee_access.cargill_business")}
            </InputLabel>
            <FormControl fullWidth>
              <Select
                sx={{
                  paddingLeft: "0",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                }}
                id="cargillBusinessEmployee"
                name="cargillBusinessEmployee"
                value={formik.values.cargillBusinessEmployee}
                onChange={formik.handleChange}
              >
                {cargillBusinessData.map(
                  (item: { name: string }, index: number) => (
                    <MenuItem key={index} value={item.name}>
                      {item.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Box>

          {/* Cargill Region */}
          <Box sx={{ textAlign: "left" }}>
            <InputLabel
              sx={{ textAlign: "left", mt: "1rem" }}
              htmlFor="employeeRegion"
            >
              {t("request_access:employee_access.region")}
            </InputLabel>
            <FormControl fullWidth>
              <Select
                sx={{
                  paddingLeft: "0",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                }}
                id="employeeRegion"
                name="employeeRegion"
                value={formik.values.employeeRegion}
                onChange={formik.handleChange}
              >
                {cargillRegionData.map(
                  (item: { name: string }, index: number) => (
                    <MenuItem key={index} value={item.name}>
                      {item.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Box>

          {/* Employee Validation Message */}
          <Box mt={6}>
            {!formik.values.validEmployee && (
              <ErrorBox>
                {t("request_access:employee_access.employee_validation_error")}
              </ErrorBox>
            )}
          </Box>
        </Box>

        {/* Accept Terms and Conditions */}

        <Box sx={{ paddingTop: "24px" }}>
          <FormGroup>
            <FormControlLabel
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              control={
                <Checkbox
                  onChange={formik.handleChange}
                  id="agreeToTerms"
                  name="agreeToTerms"
                  value={formik.values.agreeToTerms}
                />
              }
              label={
                <>
                  {t("request_access:more_information.agree_to")}{" "}
                  <Link href={termsAndConditionsUrl} target="_blank">
                    {t("request_access:more_information.terms_and_conditions")}
                  </Link>
                </>
              }
            />
          </FormGroup>
          {formik.errors.agreeToTerms && formik.touched.agreeToTerms && (
            <ErrorBox>{formik.errors.agreeToTerms}</ErrorBox>
          )}
        </Box>

        {/* reCAPTCHA */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "32px",
            marginBottom: "16px",
          }}
        >
          <DynamicRecaptcha
            sitekey={sitekey}
            language={i18n.language}
            onChange={setRecaptchaToken}
            onExpired={clearRecaptchaToken}
          />
        </Box>
        <Button
          sx={{ width: "100%" }}
          type="submit"
          disabled={!(formik.dirty && formik.isValid)}
        >
          {t("request_access:more_information.submit_request")}
        </Button>
      </form>
    </Box>
  );
}
